import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import {push} from 'react-router-redux';

import Page from 'components/Page';
import ListContent from 'components/ListContent';
import {Container} from 'containers/types';
import Loading from 'components/Loading';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import {
  withPagination,
  PAGINATION,
  WithPaginationProps
} from 'components/withPagination';
import {withFilters, FILTERS, WithFiltersProps} from 'components/withFilters';

import {SubNavigation, NotesTable, NotesListFilters} from './components';
import SalesNotesModal from 'components/SalesNotesModal';
import BookingNotesModal from 'components/BookingNotesModal';
import RoomNotesModal from 'components/RoomNotesModal';
import VisibleIf from 'components/VisibleIf';

import {getUpdatedFilter} from 'utils/filters';

import {composeActions} from 'ducks/common';
import {isModalOpen, openModal, MODALS as BASE_MODALS} from 'ducks/modals';
import {
  requiresPermission,
  requiresMultiplePermissions,
  PERMISSIONS
} from 'ducks/auth';
import {
  fetchNotes,
  selectNotes,
  selectNotesLoading,
  BOOKING_TYPE,
  ROOM_TYPE,
  SALES_TYPE
} from 'ducks/notes';
import {fetchAllRooms, selectAllRooms} from 'ducks/rooms';

import {reverse} from 'utils/urls';
import {BOOKING_DETAIL_URL} from 'config/urls';

import './styles.css';

const initialFilters = {
  search: '',
  booking: null,
  rooms: [],
  noteCategory: [],
  status: [],
  noteType: [],
  createdAtStart: null,
  createdAtEnd: null,
  resolvedAtStart: null,
  resolvedAtEnd: null
};

class NotesList extends React.Component<
  Container &
    LoginRequiredProps &
    WithPaginationProps &
    WithFiltersProps<typeof initialFilters> &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  componentDidMount() {
    const {match: {params: {locationRef}}} = this.props;

    this.props.requiresMultiplePermissions(
      {
        permissions: [
          {
            permission: PERMISSIONS.VIEW_NOTES_LIST,
            locationRef
          },
          {
            permission: PERMISSIONS.VIEW_BOOKING_NOTES,
            locationRef
          },
          {permission: PERMISSIONS.VIEW_ROOM_NOTES, locationRef}
        ],
        disableClose: false
      },
      () => {
        this.props.fetchNotes({locationRef});
        this.props.fetchAllRooms({locationRef});
      }
    );
  }

  fetchNotesExtraActions = () => {
    const {match: {params: {locationRef}}} = this.props;

    return [() => fetchNotes({locationRef})];
  };

  onPageChange = ({offset, pageSize}) => {
    const {match: {params: {locationRef}}} = this.props;

    this.props.onPaginationChange({offset, pageSize}, [
      () => fetchNotes({locationRef})
    ]);
  };

  setFilter = (field, value) => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    const filters = this.props.getFilters();

    const updatedFilter = getUpdatedFilter({field, value}, filters);

    this.props.composeActions([
      () => this.props.setFilter(updatedFilter.field, updatedFilter.value),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchNotes({locationRef})
    ]);
  };

  clearFilters = () => {
    const {locationRef} = this.props.match.params;

    this.props.composeActions([
      this.props.clearFilters,
      () => fetchNotes({locationRef})
    ]);
  };

  openBookingNotesModal = ({bookingId}: any) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_BOOKING_NOTES,
        locationRef
      },
      () => {
        this.props.openModal(BASE_MODALS.BOOKING_NOTES, {
          bookingId,
          locationRef,
          refetchExtraAction: this.fetchNotesExtraActions
        });
      }
    );
  };

  openRoomNotesModal = ({roomId}: any) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.VIEW_ROOM_NOTES
      },
      () =>
        this.props.openModal(BASE_MODALS.ROOM_NOTES, {
          roomId,
          locationRef,
          refetchExtraAction: this.fetchNotesExtraActions
        })
    );
  };

  openSalesNotesModal = ({roomId}) => {
    const {locationRef} = this.props.match.params;

    this.props.openModal(BASE_MODALS.SALES_NOTES, {
      relatedObjectId: roomId,
      locationRef,
      refetchExtraAction: this.fetchNotesExtraActions
    });
  };

  openNotesModal = ({type, relatedObjectId}: any) => {
    if (type === BOOKING_TYPE) {
      this.openBookingNotesModal({bookingId: relatedObjectId});
    }
    if (type === ROOM_TYPE) {
      this.openRoomNotesModal({roomId: relatedObjectId});
    }
    if (type === SALES_TYPE) {
      this.openSalesNotesModal({roomId: relatedObjectId});
    }
  };

  redirectToBookingDetail = ({bookingId}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        this.props.push(reverse(BOOKING_DETAIL_URL, {locationRef, bookingId}));
      }
    );
  };

  render() {
    const {
      match: {params: {locationRef}},
      notes,
      notesLoading,
      rooms,
      bookingNotesModal,
      roomNotesModal,
      salesNotesModal
    } = this.props;

    return (
      <Page activeTab="notesList" locationRef={locationRef}>
        <VisibleIf
          permission={PERMISSIONS.VIEW_NOTES_LIST}
          locationRef={locationRef}>
          <ListContent
            className="NotesList"
            filters={this.props.getFilters()}
            setFilter={this.setFilter}
            initialFilters={initialFilters}
            navigationExtraElement={SubNavigation}>
            <ListContent.Content>
              {notesLoading && <Loading />}
              {!notesLoading &&
                !_.isEmpty(notes) && (
                  <VisibleIf
                    permission={PERMISSIONS.VIEW_NOTES_LIST}
                    locationRef={locationRef}>
                    <NotesTable
                      notes={notes}
                      redirectToBookingDetail={this.redirectToBookingDetail}
                      pagination={this.props.getPagination()}
                      onPageChange={this.onPageChange}
                      openNotesModal={this.openNotesModal}
                    />
                  </VisibleIf>
                )}

              {bookingNotesModal && <BookingNotesModal />}
              {roomNotesModal && <RoomNotesModal />}
              {salesNotesModal && <SalesNotesModal />}
            </ListContent.Content>
            <ListContent.Filters>
              <NotesListFilters
                locationRef={locationRef}
                rooms={rooms}
                filters={this.props.getFilters()}
                setFilter={this.setFilter}
                clearFilters={this.clearFilters}
              />
            </ListContent.Filters>
          </ListContent>
        </VisibleIf>
      </Page>
    );
  }
}

const mapStateToProps = state => ({
  notes: selectNotes(state),
  notesLoading: selectNotesLoading(state),
  rooms: selectAllRooms(state),
  bookingNotesModal: isModalOpen(state, BASE_MODALS.BOOKING_NOTES),
  roomNotesModal: isModalOpen(state, BASE_MODALS.ROOM_NOTES),
  salesNotesModal: isModalOpen(state, BASE_MODALS.SALES_NOTES)
});

const mapDispatchToProps = {
  fetchNotes,
  composeActions,
  fetchAllRooms,
  requiresPermission,
  openModal,
  push,
  requiresMultiplePermissions
};

const componentWithPagination = withPagination(
  NotesList,
  PAGINATION.NOTES_LIST,
  {saveToUrl: true}
);

const componentWithFiltersAndPagination = withFilters(
  componentWithPagination,
  FILTERS.NOTES_LIST,
  {initialFilters, saveToUrl: true}
);

export default connect(mapStateToProps, mapDispatchToProps)(
  LoginRequired(componentWithFiltersAndPagination)
);
